import EasingAnimation from '../animation/EasingAnimation';
import IframeConfig from './IframeConfig.json';

type Orientation = 'landscape' | 'portrait';
type DeviceType = 'desktop' | 'tablet' | 'mobile';
type ScreenType = {
	width: number;
	height: number;
	orientation: string;
};

const DEFAULT_SCROLL_OPTIONS = {
	element: null,
	animate: true,
	center: true,
	minDistance: 200,
	scrollTopMargin: 10
};

/**
 * This class is used for calculating the application size when inside an iframe.
 */
export default class IframeUtility {
	/**
	 * Calculates the application height based on width.
	 *
	 * @param width Application width.
	 * @returns Calculated height.
	 */
	public static calculateHeight(width: number): number {
		const screen = this.getScreen(width);
		const orientation = <Orientation>screen.orientation;
		const deviceType = this.calculateDeviceType(screen.width, orientation);
		const appWidth = Math.min(width, IframeConfig.maxWidth);
		const availableHeight = this.getAvailableHeight(screen.height, deviceType);
		let height = appWidth / this.getRatio(orientation);

		height = Math.min(height, availableHeight, IframeConfig.maxHeightLandscape);

		if (screen.orientation === IframeConfig.orientation.landscape) {
			height = Math.max(height, this.getMinHeightLandscape(deviceType, appWidth));
		}

		return Math.floor(height);
	}

	/**
	 * Returns the device type based on the application size.
	 *
	 * @returns Device type (mobile, desktop or tablet).
	 */
	public static getDeviceType(): DeviceType {
		const minMeasurement = Math.min(window.screen.availWidth, window.screen.availHeight);
		return this.calculateDeviceType(minMeasurement, 'portrait');
	}

	/**
	 * Returns the current orientation based on width.
	 *
	 * @param width Application width.
	 * @returns Orientation (landscape or portrait).
	 */
	public static getOrientation(width: number): string {
		return this.getScreen(width).orientation;
	}

	/**
	 * Scrolls the parent window to make the element visible.
	 *
	 * @param options Options object.
	 * @param options.element Element to scroll into view.
	 * @param [options.animate] TRUE to animate. Default is TRUE.
	 * @param [options.center] TRUE to center the element. Default is TRUE.
	 * @param [options.minDistance] Minimal distance before scroll will be activated. Default is "200" (px).
	 * @param [options.scrollTopMargin] Scroll top margin that will be offset from the top of the element when the element height is greater than the screen height. Default is "10" (px);.
	 * @returns Returns the easing animation if "animate" has been set to "true".
	 */
	public static scrollToElement(options: {
		scrollTopMargin?: number;
		minDistance?: number;
		center?: boolean;
		animate?: boolean;
		element: HTMLElement;
	}): EasingAnimation | null {
		const optionsWithDefault = { ...DEFAULT_SCROLL_OPTIONS, ...options };

		if (this.isInsideIframe() && this.isParentWindowAccessible()) {
			const screenHeight = window.parent.innerHeight;
			const parentScrollPosition = this.getScrollPosition(window.parent);
			const scrollY = parentScrollPosition.y;
			const iframeTop = (window.frameElement?.getBoundingClientRect().top ?? 0) + scrollY;
			const elementTop =
				optionsWithDefault.element.getBoundingClientRect().top +
				this.getScrollPosition().y +
				iframeTop;
			const elementHeight = optionsWithDefault.element.offsetHeight;
			const centeredElementScrollY =
				elementHeight > screenHeight
					? elementTop - optionsWithDefault.scrollTopMargin
					: elementTop - screenHeight / 2 + elementHeight / 2 - optionsWithDefault.scrollTopMargin;
			const elementScrollY = optionsWithDefault.center
				? centeredElementScrollY
				: elementTop - optionsWithDefault.scrollTopMargin;
			const newScrollY = elementScrollY > 0 ? elementScrollY : 0;
			const distanceY = newScrollY < scrollY ? scrollY - newScrollY : newScrollY - scrollY;

			if (optionsWithDefault.animate) {
				const frames = Math.floor(distanceY / 10);
				if (distanceY > optionsWithDefault.minDistance || elementHeight > screenHeight) {
					const animation = new EasingAnimation();

					animation.on('frame', (y) => window.parent.scrollTo(parentScrollPosition.x, y));
					animation.startValue = scrollY;
					animation.endValue = newScrollY;
					animation.frames = frames;
					animation.start();

					return animation;
				}
			} else {
				window.parent.scrollTo(parentScrollPosition.x, newScrollY);
			}
		}

		return null;
	}

	/**
	 * Checks if the application is inside an iframe.
	 *
	 * @returns TRUE if inside an iframe.
	 */
	public static isInsideIframe(): boolean {
		try {
			if (window.parent === window) {
				return false;
			}
		} catch (error) {
			// Ignore error
		}
		return true;
	}

	/**
	 * Checks if the parent window is accessible and not subject to cross-origin issues.
	 *
	 * @returns TRU if the parent window is accessible.
	 */
	public static isParentWindowAccessible(): boolean {
		try {
			window.parent.document;
		} catch (error) {
			return false;
		}
		return true;
	}

	/**
	 * Returns parent scroll position.
	 *
	 * @param frame Frame object. Defaults to "window".
	 * @returns Scroll position.
	 */
	protected static getScrollPosition(frame: Window = window): {
		y: number;
		x: number;
	} {
		return {
			y:
				frame.pageYOffset !== undefined
					? frame.pageYOffset
					: frame.document.documentElement.scrollTop,
			x:
				frame.pageXOffset !== undefined
					? frame.pageXOffset
					: frame.document.documentElement.scrollLeft
		};
	}

	/**
	 * Returns the current screen data: width, height and orientation.
	 *
	 * @param width Application width.
	 * @returns Screen setup.
	 */
	protected static getScreen(width: number): ScreenType {
		/*
		 * Some devices may return same availWidth/availHeight despite rotation
		 * using given iframe width and assuming that it will not be greater that the actual screen width,
		 * we can assume that we are in portrait if the iframe width is smaller or same as the smallest of the values
		 */
		if (
			width <=
			Math.min(window.screen.availWidth, window.screen.availHeight, IframeConfig.minWidthLandscape)
		) {
			return {
				width: Math.min(window.screen.availWidth, window.screen.availHeight),
				height: Math.max(window.screen.availWidth, window.screen.availHeight),
				orientation: IframeConfig.orientation.portrait
			};
		} else {
			return {
				width: Math.max(window.screen.availWidth, window.screen.availHeight),
				height: Math.min(window.screen.availWidth, window.screen.availHeight),
				orientation: IframeConfig.orientation.landscape
			};
		}
	}

	/**
	 * Returns the available height.
	 *
	 * @param screenHeight The screen height.
	 * @param deviceType Device type (mobile, desktop or tablet).
	 * @returns Available height.
	 */
	protected static getAvailableHeight(screenHeight: number, deviceType: DeviceType): number {
		return screenHeight - IframeConfig.unavailableHeight[deviceType];
	}

	/**
	 * Calculates the device type.
	 *
	 * @param screenWidth The screen width.
	 * @param orientation Orientation (portrait or landscape).
	 * @returns Device type (mobile, desktop or tablet).
	 */
	protected static calculateDeviceType(screenWidth: number, orientation: Orientation): DeviceType {
		if (screenWidth < IframeConfig.screenWidthLimits[orientation].mobile) {
			// Largest known phone is 736 (iPhone 6+), smallest known tablet is 1024 (iPad)
			return 'mobile';
		}
		if (screenWidth <= IframeConfig.screenWidthLimits[orientation].tablet) {
			// Tablets larger than this may use full desktop ui
			return 'tablet';
		}
		return 'desktop';
	}

	/**
	 * Returns the minimal possible height when in landscape.
	 *
	 * @param deviceType Device type (mobile, desktop or tablet).
	 * @param appWidth Application width.
	 * @returns Minimal height for landscape.
	 */
	protected static getMinHeightLandscape(deviceType: DeviceType, appWidth: number): number {
		if (deviceType == IframeConfig.deviceType.mobile) {
			return appWidth / IframeConfig.maxRatioLandscape;
		}
		return IframeConfig.minHeightLandscape;
	}

	/**
	 * Returns the current ratio.
	 *
	 * @param orientation Orientation (portrait or landscape).
	 * @returns Ratio.
	 */
	protected static getRatio(orientation: Orientation): number {
		const ratio = IframeConfig.preferredRatio[orientation].split('/');
		return parseInt(ratio[0]) / parseInt(ratio[1]);
	}
}
